import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Box, useTheme } from '@mui/material';
import { isDarkPagePalette } from '@theme/palettes/getPalette';
import { keyframes } from '@emotion/react';

const BULB_SPACING = 34;
const BULB_COLORS = ['#ff4a4a', '#3fd06b', '#ffcf3d', '#4ab3ff'];
const KNOCKED_OUT_FOR = 1_600;
const WIRE_COLOR = '#233227';
// the dark wire disappears against a dark page palette's navy header
const WIRE_COLOR_ON_DARK = '#557a5e';
// the bulb's cap pokes up this far above the bulb, which is where the wire holds it
const CAP_HEIGHT = 3;
// how much further the wire droops between two bulbs than the higher of the two
const SAG = 3;

const twinkle = keyframes`
  0%, 60% { opacity: 1; }
  100% { opacity: 0.45; box-shadow: 0 0 3px 0 var(--bulb-color); }
`;

// fixed per bulb index so the twinkle pattern doesn't reshuffle on every re-render
const bulbTiming = (index: number) => ({
  duration: 0.9 + ((index * 37) % 15) / 10,
  delay: ((index * 53) % 20) / 10,
});

// how far below the strand's top each bulb's cap hangs: a slow wave plus a faster, out-of-step one, so the
// strand looks hand-hung rather than ruled
const hangAt = (index: number, count: number) =>
  5 + Math.sin((index / count) * Math.PI * 6) * 2.5 + Math.sin(index * 2.3) * 1.5;

// the wire, in a viewBox one unit per bulb wide: through each cap, drooping between neighbours
const wirePath = (hangs: number[]) => {
  const first = hangs[0] ?? 0;
  const last = hangs[hangs.length - 1] ?? 0;
  let d = `M 0 ${first} L 0.5 ${first}`;
  for (let i = 1; i < hangs.length; i++) {
    d += ` Q ${i} ${Math.max(hangs[i - 1], hangs[i]) + SAG} ${i + 0.5} ${hangs[i]}`;
  }
  return `${d} L ${hangs.length} ${last}`;
};

/**
 * Christmas: a strand of twinkling bulbs hanging from the bottom edge of the header.
 * Click a bulb to knock it out for a moment. Must be rendered inside a positioned header container.
 * `top` is where the wire hangs; the default clears the large home-page logo, which overhangs the header.
 */
export const StringLights = ({ top = 'calc(100% - 2px)' }: { top?: string }) => {
  const [bulbCount, setBulbCount] = useState(() => Math.ceil(window.innerWidth / BULB_SPACING));
  const [knockedOut, setKnockedOut] = useState<Set<number>>(new Set());

  useEffect(() => {
    const onResize = () => setBulbCount(Math.ceil(window.innerWidth / BULB_SPACING));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const knockOut = (index: number) => {
    setKnockedOut((current) => new Set(current).add(index));
    setTimeout(() => {
      setKnockedOut((current) => {
        const next = new Set(current);
        next.delete(index);
        return next;
      });
    }, KNOCKED_OUT_FOR);
  };

  const theme = useTheme();
  const hangs = useMemo(() => Array.from({ length: bulbCount }, (_, i) => hangAt(i, bulbCount)), [bulbCount]);

  return (
    <Box sx={[sxStyles.strand, { top }]} aria-hidden>
      {/* bulbs sit at the centre of equal slots (space-around), so x = index + 0.5 in a bulbCount-wide viewBox */}
      <Box component='svg' sx={sxStyles.wire} viewBox={`0 0 ${bulbCount} 20`} preserveAspectRatio='none'>
        <path
          d={wirePath(hangs)}
          fill='none'
          stroke={isDarkPagePalette(theme.palette) ? WIRE_COLOR_ON_DARK : WIRE_COLOR}
          strokeWidth={1.5}
          vectorEffect='non-scaling-stroke'
        />
      </Box>
      {Array.from({ length: bulbCount }, (_, i) => {
        const { duration, delay } = bulbTiming(i);
        const isOut = knockedOut.has(i);
        return (
          <Box
            key={i}
            component='button'
            type='button'
            tabIndex={-1}
            onClick={() => knockOut(i)}
            style={{ '--bulb-color': BULB_COLORS[i % BULB_COLORS.length] } as CSSProperties}
            sx={[
              sxStyles.bulb,
              // hang from the wire by the cap
              { top: hangs[i] + CAP_HEIGHT },
              isOut
                ? { background: '#555', boxShadow: 'none', animation: 'none' }
                : { animation: `${twinkle} ${duration}s ease-in-out ${delay}s infinite alternate` },
            ]}
          />
        );
      })}
    </Box>
  );
};

const sxStyles = {
  strand: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 18,
    display: 'flex',
    justifyContent: 'space-around',
    pointerEvents: 'none',
    // above the quote page's sticky tab bar (1000)
    zIndex: 1001,
  },
  wire: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: 20,
    overflow: 'visible',
  },
  bulb: {
    position: 'relative',
    width: 7,
    height: 11,
    padding: 0,
    border: 0,
    borderRadius: '45% 45% 50% 50% / 35% 35% 65% 65%',
    background: 'var(--bulb-color)',
    boxShadow: '0 0 8px 2px var(--bulb-color)',
    cursor: 'pointer',
    pointerEvents: 'auto',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -3,
      left: 1.5,
      width: 4,
      height: 4,
      borderRadius: '1px',
      background: '#3a463c',
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
};
